import { AppStore } from "@/store";
import { logger } from "@/modules/logger";
import { toastModule } from "@/modules/toast";
import { objectModule } from "@/modules/object";
import { TrackedEvent, trackEvent } from "@/domains/metrics";
import { VoiceSessionRefineSuggestionData, VoiceSessionSuggestionData } from "@/store/note/types";
import { truncateSuggestionDataForTracking } from "@/store/note/helpers/suggestionTracking";
import { refineNoteWithTranscript } from "@/actions/refineNoteWithTranscript";

export const refineNoteWithVoice = async ({
  noteId,
  userPrompt,
  audioRecordingId,
  capturedAudioId,
  suggestionData,
  store,
}: {
  noteId: string;
  userPrompt: string;
  audioRecordingId: string;
  capturedAudioId: string;
  suggestionData: VoiceSessionSuggestionData | VoiceSessionRefineSuggestionData;
  store: AppStore;
}) => {
  const startTime = Date.now();

  try {
    const result = await refineNoteWithTranscript({
      noteId,
      audioRecordingId,
      capturedAudioId,
      transcriptContent: suggestionData.metadata.transcript_text,
      associatedSessionId: suggestionData.metadata.session_id,
      activeSuggestionMarkdownContent: suggestionData.suggested_markdown_content,
      userPrompt,
      store,
    });
    if (!result.success) {
      throw new Error(result.error);
    }

    const endTime = Date.now();
    const elapsedTime = Math.floor((endTime - startTime) / 1000);
    const { previousMarkdownContents, suggestedMarkdownContents, transcriptText } =
      truncateSuggestionDataForTracking(suggestionData);

    trackEvent(TrackedEvent.VoiceSessionSuggestedEditsRefine, {
      session_id: suggestionData.metadata.session_id,
      captured_audio_id: capturedAudioId,
      note_id: noteId,
      elapsed_time: elapsedTime,
      previous_title: suggestionData.prev_primary_label,
      previous_markdown_contents: previousMarkdownContents,
      transcript: transcriptText,
      suggested_markdown_contents: suggestedMarkdownContents,
      suggested_title: suggestionData.suggested_primary_label,
      user_prompt: userPrompt,
    });

    return { success: true };
  } catch (e: unknown) {
    logger.error({
      message: "Failed to refine note",
      info: {
        error: objectModule.safeErrorAsJson(e as Error),
        noteId,
        userPrompt,
        audioRecordingId,
        capturedAudioId,
        suggestionKind: "VOICE_SESSION_V1",
      },
    });
    toastModule.triggerToast({ content: "Failed to refine note. Please try again." });
    return { success: false, error: e instanceof Error ? e.message : "Unknown error" };
  }
};
